import { defineStore } from "pinia";
import { ref } from "vue";
import { useCloudClientStore } from "@/stores/cloud-client.js";
import { UAParser } from "ua-parser-js";
import { Crisp } from "crisp-sdk-web";
import { is } from "@vee-validate/rules";

export const useCompatibilityCheckStore = defineStore(
  "compatibilityCheck",
  () => {
    const hardwareSpecifications = ref({});

    const cloudClient = useCloudClientStore();

    const tests = ref([
      {
        title: "Connexion internet",
        check: async () => {
          let data = null;

          const promises = [];

          const promise = async () => {
            const networkData = await speedTest();

            if (networkData) {
              const quality = await getSessionQuality(
                networkData.networkPing.value,
                networkData.networkBandwidth.value
              );
              if (quality === "FAILED") {
                data = {
                  name: "error",
                  message: "Connexion impossible",
                };
              } else {
                data = {
                  name: "success",
                  message: "Connexion stable",
                };
              }

              data = {
                ...data,
                data: { ...networkData },
              };

              hardwareSpecifications.value = {
                ...hardwareSpecifications.value,
                ...networkData,
              };
            }
          };

          promises.push(promise);

          await Promise.all(promises.map((asyncFn) => asyncFn()));

          return data;
        },
        result: null,
      },
      {
        title: "Navigateur",
        check: async () => {
          let data = null;

          const promise = new Promise((resolve) => {
            const browserData = browserTest();

            const compatibleBrowser = ["Opera", "Chromium", "Firefox", "Chrome"];
            const compatibleMobileBrowser = [];
            const isSafariEnabled = import.meta.env.VITE_ALLOW_SAFARI === "true";
            const isMobileEnabled = import.meta.env.VITE_ALLOW_MOBILE === "true";
            if (isSafariEnabled) {
              compatibleBrowser.push("Safari");
              if (isMobileEnabled && !cloudClient.isV2()) {
                compatibleMobileBrowser.push("Mobile Safari");
              }
            }
            if (isMobileEnabled && !cloudClient.isV2()) {
              compatibleMobileBrowser.push("Mobile Chrome");
              compatibleMobileBrowser.push("Android Browser");
              compatibleMobileBrowser.push("Samsung Internet");
            }
            const checkBrowser = ["Edge"];
            const checkBrowserVersions = { "Edge": [79, 0, 309] };

            if (browserData) {
              if (compatibleBrowser.includes(browserData.browserAgent.value) ||
              (!cloudClient.isV2() && isMobileEnabled && compatibleMobileBrowser.includes(browserData.browserAgent.value))) {
                data = {
                  name: "success",
                  message: "Navigateur compatible",
                };
              } else if (checkBrowser.includes(browserData.browserAgent.value)) {
                // check if version is ok
                const minVersion = checkBrowserVersions[browserData.browserAgent.value];
                const [major, minor, patch] = browserData.browserVersion.value.split('.').map(Number);

                var validVersion = false;
                if (major > minVersion[0]) {
                  validVersion = true;
                }
                else if (major === minVersion[0] && minor > minVersion[1]) {
                  validVersion = true;
                }
                else if (major === minVersion[0] && minor === minVersion[1] && patch >= minVersion[3]) {
                  validVersion = true;
                }

                if (validVersion) {
                  data = {
                    name: "success",
                    message: "Navigateur compatible",
                  };
                }
                else {
                  data = {
                    name: "error",
                    message: "Navigateur incompatible",
                  };
                }
              } else {
                data = {
                  name: "error",
                  message: "Navigateur incompatible",
                };
              }

              data = {
                ...data,
                data: { ...browserData },
              };

              hardwareSpecifications.value = {
                ...hardwareSpecifications.value,
                ...browserData,
              };
            }
            resolve();
          });

          return promise.then(() => {
            return data;
          });
        },
        result: null,
      },
      {
        title: "Performances",
        check: async () => {
          let data = null;

          const promise = new Promise((resolve) => {
            (async () => {
              const performanceData = await perfomancesTest();
              data = {
                name: "success",
                message: "Performances suffisantes",
                data: { ...performanceData },
              };

              hardwareSpecifications.value = {
                ...hardwareSpecifications.value,
                ...performanceData,
              };

              resolve();
            })();
          });

          return promise.then(() => {
            return data;
          });
        },
        result: null,
      },
      {
        title: "Taille de la fenêtre",
        check: async () => {
          let data = null;
          const promise = new Promise((resolve) => {
            const isMobileEnabled = import.meta.env.VITE_ALLOW_MOBILE === "true";
            const screenData = screenTest();
            if (screenData) {
              if (!isMobileEnabled && (
                screenData.screenWidth.value < 900 ||
                screenData.screenHeight.value < 500
              )) {
                data = {
                  name: "bad",
                  message: "Fenêtre trop petite",
                };
              } else if (
                screenData.screenRatio.value < 1 ||
                screenData.screenRatio.value > 2.5 ||
                screenData.screenHeight.value < 800
              ) {
                data = {
                  name: "warn",
                  message: "Fenêtre acceptable",
                };
              } else {
                data = {
                  name: "success",
                  message: "Fenêtre adaptée",
                };
              }

              data = {
                ...data,
                data: { ...screenData },
              };

              hardwareSpecifications.value = {
                ...hardwareSpecifications.value,
                ...screenData,
              };
            }
            resolve();
          });

          return promise.then(() => {
            return data;
          });
        },
        result: null,
      },
      {
        title: "Instance",
        check: async () => {
          let data = null;

          const res = await cloudClient.requestReservation();
          const instanceDisponibility = {
              value: "Disponible",
              label: "Disponibilité",
          };
          if (res.available) {
            data = {
              name: "success",
              message: "Cloud disponible",
              data: {
                instanceDisponibility,
              },
            };
            if (res.started) {
              data.message = "Session en cours";
            }
          } else {
            instanceDisponibility.value = "Non disponible";
            var message = "";
            if (res.reason && res.reason === "out_of_support") {
              message = "Version de la campagne non supportée";
            } else if (res.maintenance) {
              message = "Cloud en maintenance";
            }
            else if (res.error) {
              message = "Erreur momentanée";
            }
            else if (res.started) {
              message = "Session en cours";
            }
            else {
              message = "Cloud indisponible";
            }

            data = {
              name: "error",
              message: message,
              data: {
                instanceDisponibility,
              },
            };
          }
          

          hardwareSpecifications.value = {
            ...hardwareSpecifications.value,
            instanceDisponibility,
          };

          return data;
        },
        result: null,
      },
    ]);

    async function speedTest() {
      try {
        // Get the average ping
        const pingResult = await pingTest();
        console.log("Ping result:", pingResult.networkPing.value);
        // Get the bandwidth
        const bandwidthResult = await bandwidthTest();
        console.log("Bandwidth result:", bandwidthResult.networkBandwidth.value);
        // Send results to operator then wait for response
        return {
          ...pingResult,
          ...bandwidthResult
        };
      }
      catch (error) {
        return {
          networkPing: {
            value: -1,
            label: "Ping/ms",
          },
          networkBandwidth: {
            value: -1,
            label: "Bandwidth/Mbps",
          },
        };
      }
    }

    async function pingTest() {
      let latency = -1;

      try {
        const promise = new Promise((resolve, reject) => {

          // Set a timeout to handle cases where events do not trigger as expected
          let timeout = null;
          cloudClient.emitter.off("onCancelSpeedtest");
          cloudClient.emitter.on("onCancelSpeedtest", () => {
            reject(new Error("Cancelling Speedtest as there is no capacity"));
          });
          cloudClient.emitter.off("onSpeedTestURLAvailable");
          cloudClient.emitter.on("onSpeedTestURLAvailable", () => {
            try {
              timeout = setTimeout(() => {
                reject(new Error("Ping test timed out"));
              }, 15000); // Timeout after 15 seconds
              cloudClient.emitter.off("onCloudOperatorLatencyUpdated");
              cloudClient.emitter.on(
                "onCloudOperatorLatencyUpdated",
                (new_latency) => {
                  latency = new_latency;
                  clearTimeout(timeout); // Clear timeout on latency recieved
                  resolve(latency);
                }
              );
              cloudClient.startPingTest();
            } catch (error) {
              clearTimeout(timeout);
              reject(new Error("Error starting ping test or setting up latency listener"));
            }
          });
        });

        latency = await promise;

        return {
          networkPing: {
            value: Math.round(latency),
            label: "Ping/ms",
          },
        };
      } catch (error) {
        console.error("Error in pingTest:", error);
        return {
          networkPing: {
            value: -1,
            label: "Ping/ms"
          },
        };
      }
    }

    async function bandwidthTest() {
      let speedInMbps = -1;
      try {
        const promise = new Promise((resolve) => {
          cloudClient.emitter.off("onCloudOperatorBandwidthUpdated");
          cloudClient.emitter.on(
            "onCloudOperatorBandwidthUpdated",
            (new_speedInMbps) => {
              speedInMbps = new_speedInMbps;
              resolve();
            }
          );
          cloudClient.startDownloadTest();
        });

        return promise.then(() => {
          return {
            networkBandwidth: {
              value: Math.round(speedInMbps),
              label: "Bandwidth/Mbps",
            },
          };
        });
      } catch (error) {
        console.error("Error in bandwidthTest:", error);
        return {
          networkBandwidth: {
            value: -1,
            label: "Bandwidth/Mbps"
          },
        };
      }
    }

    async function getSessionQuality(ping, bandwidth) {
      try {
          if (await av1Supported()) {
            console.log("AV1 is supported, sending codec AV1");
            return await cloudClient.sendSpeedTestResults(ping, bandwidth, "AV1");
          }
          console.log("AV1 is not supported, sending codec H264");
          return await cloudClient.sendSpeedTestResults(ping, bandwidth);
      } catch (error) {
        console.error("Could not retrieve session quality:", error);
        return "FAILED";
      }
    }

    async function av1Supported() {
      try {
        if (!("mediaCapabilities" in navigator)) {
          return false;
        }
        const av1Config = {
          type: "webrtc",
          video: {
            contentType: "video/av1",
            width: 1920,
            height: 1080,
            bitrate: 12_000_000,
            framerate: 60,
          },
        };
        const result = await navigator.mediaCapabilities.decodingInfo(av1Config);
        return result.supported && result.smooth && result.powerEfficient;
      } catch (error) {
        console.error("Error checking AV1 support:", error);
        return false;
      }
    }

    function browserTest() {
      if (navigator) {
        const { browser } = UAParser(navigator.userAgent);

        return {
          browserAgent: {
            value: browser.name,
            label: "Application logicielle",
          },
          browserVersion: {
            value: browser.version,
            label: "Version",
          },
        };
      }

      return null;
    }

    async function perfomancesTest() {
      if (navigator) {
        let osObj;

        const parser = new UAParser(navigator.userAgent);

        if (import.meta.env.PROD) {
          const osParser = await parser.getOS().withClientHints();

          osObj = {
            platform: osParser.name,
            platformVersion: osParser.version,
          };
        } else {
          osObj = {
            platform: parser.getOS().name,
            platformVersion: parser.getOS().version,
          };
        }

        return {
          hardwareRam: {
            value:
              navigator.deviceMemory == 8
                ? `Min. ${navigator.deviceMemory}`
                : navigator.deviceMemory,
            label: "RAM",
          },
          hardwareCpuCores: {
            value: navigator.hardwareConcurrency,
            label: "CPU cores",
          },
          hardwareOs: {
            value: `${osObj.platform} ${osObj.platformVersion}`,
            label: "OS",
          },
        };
      }

      return null;
    }

    function screenTest() {
      if (window) {
        return {
          screenWidth: {
            value: `${window.innerWidth}`,
            label: "Largeur d'écran (px)",
          },
          screenHeight: {
            value: `${window.innerHeight}`,
            label: "Hauteur d'écran (px)",
          },
          screenRatio: {
            value: parseFloat(
              (window.innerWidth / window.innerHeight).toFixed(2)
            ),
            label: "Ratio",
          },
        };
      }

      return null;
    }

    async function launchTests() {
      const promises = [];

      tests.value.forEach((test) => {
        const promise = async () => {
          test.result = await test.check();
        };

        promises.push(promise);
      });

      await Promise.all(promises.map((checkFn) => checkFn()));
    }

    function resetCheck() {
      tests.value.forEach((test) => {
        test.result = null;
      });
    }

    return {
      hardwareSpecifications,
      tests,
      av1Supported,
      launchTests,
      resetCheck,
    };
  }
);
